import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "../lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import { useTheme } from "../context/ThemeContext";
import { 
  Menu, 
  X, 
  ChevronRight,
  Code,
  Sun,
  Moon
} from "lucide-react";

// Animated underline component for active links
const AnimatedUnderline = ({ isActive }) => {
  if (!isActive) return null;
  
  return (
    <motion.div 
      className="absolute left-0 right-0 bottom-0 h-0.5 bg-gradient-to-r from-blue-600 to-violet-600 rounded-full"
      layoutId="navbar-underline"
      transition={{ type: "spring", stiffness: 350, damping: 30 }}
    />
  );
};

const NavLink = ({ label, to, isActive, onClick, className }) => (
  <Link
    onClick={onClick}
    className={cn(
      "relative px-4 py-2 text-sm font-medium transition-colors hover:text-zinc-900 dark:hover:text-zinc-100",
      isActive 
        ? "text-zinc-900 dark:text-zinc-100" 
        : "text-zinc-600 dark:text-zinc-400",
      className
    )}
    to={to}
  >
    {label}
    <AnimatedUnderline isActive={isActive} />
  </Link>
);

const Navbar = () => {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  console.log("Current theme:", theme);

  // Determine active route based on current location
  const getActiveRoute = () => {
    const path = location.pathname;
    if (path === "/") return "home";
    if (path.startsWith("/projects")) return "projects";
    if (path.startsWith("/contact")) return "contact";
    return "home";
  };

  const active = getActiveRoute();

  // Handle scroll event to change navbar appearance
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    
    // Initial check
    handleScroll();
    
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleNavLinkClick = () => {
    setIsOpen(false);
  };

  return (
    <header className={cn(
      "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
      isScrolled 
        ? "bg-white/90 dark:bg-zinc-900/90 backdrop-blur-md shadow-sm border-b border-zinc-200 dark:border-zinc-800 py-3" 
        : "bg-transparent py-5"
    )}>
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2 text-lg font-bold text-zinc-900 dark:text-white z-50"
            onClick={() => handleNavLinkClick("home")}
          >
            <div className="bg-gradient-to-r from-blue-600 to-violet-600 text-white p-1.5 rounded">
              <Code size={20} />
            </div>
            <span>Casper De Bock</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            <NavLink
              label="Home"
              to="/"
              isActive={active === "home"}
              onClick={() => handleNavLinkClick("home")}
            />
            <NavLink
              label="Projects"
              to="/projects"
              isActive={active === "projects"}
              onClick={() => handleNavLinkClick("projects")}
            />
            <NavLink
              label="Contact"
              to="/contact"
              isActive={active === "contact"}
              onClick={() => handleNavLinkClick("contact")}
            />

            {/* Theme Toggle Button */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-md text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:text-zinc-100 dark:hover:bg-zinc-800 transition-colors ml-1"
              aria-label={theme === 'dark' ? "Switch to light mode" : "Switch to dark mode"}
            >
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={theme}
                  initial={{ y: -10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 10, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {theme === 'dark' ? (
                    <Sun className="h-5 w-5" />
                  ) : (
                    <Moon className="h-5 w-5" />
                  )}
                </motion.div>
              </AnimatePresence>
            </button>
            
            <div className="ml-3">
              <Button asChild size="sm" className="bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white rounded-lg shadow-sm transition-all">
                <Link to="/contact" className="flex items-center gap-1.5">
                  Let's Talk
                  <ChevronRight className="h-3.5 w-3.5" />
                </Link>
              </Button>
            </div>
          </nav>

          {/* Mobile Menu and Theme Toggle */}
          <div className="flex items-center md:hidden">
            {/* Theme Toggle Button for Mobile */}
            <button
              onClick={toggleTheme}
              className="p-2 mr-1 rounded-md text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:text-zinc-100 dark:hover:bg-zinc-800 transition-colors z-50"
              aria-label={theme === 'dark' ? "Switch to light mode" : "Switch to dark mode"}
            >
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={theme}
                  initial={{ y: -10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 10, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {theme === 'dark' ? (
                    <Sun className="h-5 w-5" />
                  ) : (
                    <Moon className="h-5 w-5" />
                  )}
                </motion.div>
              </AnimatePresence>
            </button>

            {/* Mobile Menu Button */}
            <button 
              onClick={toggleMenu} 
              className="p-2 rounded-md text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:text-zinc-100 dark:hover:bg-zinc-800 transition-colors z-50"
              aria-label={isOpen ? "Close menu" : "Open menu"}
            >
              {isOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            className="fixed inset-0 bg-white dark:bg-zinc-900 z-40 md:hidden"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
          >
            <div className="flex flex-col items-center justify-center h-full space-y-6">
              <NavLink
                label="Home"
                to="/"
                isActive={active === "home"}
                onClick={handleNavLinkClick}
                className="text-xl"
              />
              <NavLink
                label="Projects"
                to="/projects"
                isActive={active === "projects"}
                onClick={handleNavLinkClick}
                className="text-xl"
              />
              <NavLink
                label="Contact"
                to="/contact"
                isActive={active === "contact"}
                onClick={handleNavLinkClick}
                className="text-xl"
              />
              <div className="mt-4">
                <Button asChild size="lg" className="bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white rounded-lg shadow-lg transition-all">
                  <Link to="/contact" onClick={handleNavLinkClick} className="flex items-center gap-2">
                    Let's Work Together
                    <ChevronRight className="h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;